import { useMutation, useQueryClient, QueryClient } from '@tanstack/react-query'
import {
  postBasicResultReport,
  postMedicationReport,
  postRPUnitReport,
} from '../../../../apis/reportPresc'
import { queryKeys } from '../../../../shared'
import { Prescription } from '../../../../types/models/Presc'
import type { BasicResultFieldKey } from '../fields'
import { patchRemotePrescription } from '../remoteInput/useRemoteInput'

/** latest が報告できる RP 単位の項目。ItemsPane の行と 1:1 で対応する。 */
export type ReportableRpKey =
  | '用法名称'
  | 'コメント'
  | '調剤数量'
  | '医薬品取得'

/** latest が報告できる薬剤行の項目。ItemsPane の行と 1:1 で対応する。 */
export type ReportableMedKey = '薬品名称' | '用量'

type Report =
  | { scope: 'basic'; key: BasicResultFieldKey }
  | { scope: 'rp'; rpIndex: number; key: ReportableRpKey }
  | { scope: 'med'; rpIndex: number; medIndex: number; key: ReportableMedKey }

type ReportVars = Report & { prescId: string; isReported: boolean }

/**
 * 報告した 1 項目だけを差し替えた処方箋を返す。触らない欄は元の参照のまま返す。
 *
 * **`presc_images` を作り直さないことがこの関数の要件**。参照が変わると ImagePane が
 * 表示中の 1 枚を別物と見なす(ImagePane の PageView)。
 */
export const withBasicResultReported = (
  prescription: Prescription,
  key: BasicResultFieldKey,
  isReported: boolean
): Prescription => {
  const basic = prescription.basic_result
  // JAHIS QR 直読は basic_result 自体が無く、報告する行も描画されないため到達しない。
  if (!basic) return prescription
  return {
    ...prescription,
    basic_result: {
      ...basic,
      [key]: { ...basic[key], is_reported: isReported },
    },
  }
}

/** RP 単位の項目版。対象の RP だけを差し替え、ほかの RP は元の参照のまま返す。 */
export const withRpUnitReported = (
  prescription: Prescription,
  rpIndex: number,
  key: ReportableRpKey,
  isReported: boolean
): Prescription => {
  const rpUnits = prescription.med_result
  if (!rpUnits) return prescription
  return {
    ...prescription,
    med_result: rpUnits.map((rp, index) =>
      index === rpIndex
        ? { ...rp, [key]: { ...rp[key], is_reported: isReported } }
        : rp
    ),
  }
}

/** 薬剤行の項目版。対象の RP の対象の行だけを差し替える。 */
export const withMedicationReported = (
  prescription: Prescription,
  rpIndex: number,
  medIndex: number,
  key: ReportableMedKey,
  isReported: boolean
): Prescription => {
  const rpUnits = prescription.med_result
  if (!rpUnits) return prescription
  return {
    ...prescription,
    med_result: rpUnits.map((rp, index) =>
      index === rpIndex
        ? {
            ...rp,
            med_result: rp.med_result.map((med, medPosition) =>
              medPosition === medIndex
                ? { ...med, [key]: { ...med[key], is_reported: isReported } }
                : med
            ),
          }
        : rp
    ),
  }
}

const REPORT_FAILED = '報告できませんでした'

/** 同じ行を指しているか。scope ごとに見るものが違うので畳まずに書く。 */
const isSameTarget = (a: Report, b: Report): boolean => {
  if (a.scope === 'basic' && b.scope === 'basic') return a.key === b.key
  if (a.scope === 'rp' && b.scope === 'rp')
    return a.rpIndex === b.rpIndex && a.key === b.key
  if (a.scope === 'med' && b.scope === 'med')
    return (
      a.rpIndex === b.rpIndex && a.medIndex === b.medIndex && a.key === b.key
    )
  return false
}

const applyReport = (
  prescription: Prescription,
  report: ReportVars
): Prescription => {
  switch (report.scope) {
    case 'basic':
      return withBasicResultReported(
        prescription,
        report.key,
        report.isReported
      )
    case 'rp':
      return withRpUnitReported(
        prescription,
        report.rpIndex,
        report.key,
        report.isReported
      )
    case 'med':
      return withMedicationReported(
        prescription,
        report.rpIndex,
        report.medIndex,
        report.key,
        report.isReported
      )
  }
}

/**
 * 一覧キャッシュの中の 1 件だけを差し替える。ほかの処方箋は元の参照のまま置く。
 *
 * キャッシュの住所は共有の `queryKeys` を使う。latest と legacy は同じ 1 本の query
 * (`usePrescriptionsQuery`)を見ているので、鍵を latest 側で作り直すと別のキャッシュを
 * 指してしまい、書いても画面に出ない。
 *
 * 頼まれた紙(遠隔入力)は自店の一覧に無く、5 秒の口のキャッシュにしか居ないので、そちらも同じ要領で書く。
 */
const patchCachedPrescription = (
  queryClient: QueryClient,
  prescId: string,
  report: ReportVars
) => {
  queryClient.setQueryData<Prescription[]>(
    queryKeys.prescriptions.list(),
    (prescriptions) =>
      prescriptions?.map((presc) =>
        presc.id === prescId ? applyReport(presc, report) : presc
      )
  )
  patchRemotePrescription(queryClient, prescId, (presc) =>
    applyReport(presc, report)
  )
}

/**
 * 報告(旗)の送信。**共有の `useReportCallbacks` を呼ばず、latest がキャッシュの更新を持つ。**
 *
 * 共有側は成功時に `resetPrescriptionsCache` = `GET /prescriptions` で一覧を全件取り直す。
 * この取り直しが画面のちらつきの直接の原因になっている:
 *
 * - backend の `PrescImageSerializer.get_image_url` は CloudFront の署名付き URL を返し、
 *   有効期限に `now` が入る(common/storage.py `generate_signed_url`)。**同じ画像でも
 *   取得のたびに URL が変わる**
 * - URL が変わると react-query の構造共有(`replaceEqualDeep`)が効かず、一覧のほぼ全件が
 *   別オブジェクトになる。詳細の `<img src>` も変わるので画像が再取得される
 *
 * 報告が backend でやることは `basic_result[key].is_reported` の**その場書き換え 1 個だけ**で、
 * 版も id も増えない(prescription/views.py `BasicResultReportView` 他。`save_prescription` は
 * `presc.save()` のみ)。`PrescriptionSerializer` の他のフィールドに `is_reported` から
 * 派生するものは無い(`was_error` は file_to_presc_error と selected_option から出る)。
 * **したがってキャッシュの一点書き換えは全件取り直しと結果が一致し、往復も 1 回で済む。**
 *
 * latest が共有し続けるのは**送り先の形**(`apis/reportPresc`)と**キャッシュの住所**
 * (`queryKeys`)だけで、どちらも振る舞いを持たない。`useEditField` が共有 API
 * `apis/editPresc` を使いつつ戻し方だけ latest に持つのと同じ線(ADR-0034)。
 *
 * 送信が失敗したときはキャッシュを書かない。旗は押す前のままになる
 * (押せていないことがそのまま見える。共有側も結果は同じで、握り潰して console に出すだけ)。
 */
export const useReportField = (prescription: Prescription) => {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (report: ReportVars) => {
      const { prescId, isReported } = report
      switch (report.scope) {
        case 'basic':
          return postBasicResultReport({ prescId, key: report.key, isReported })
        case 'rp':
          return postRPUnitReport({
            prescId,
            rpNum: report.rpIndex,
            key: report.key,
            isReported,
          })
        case 'med':
          return postMedicationReport({
            prescId,
            rpNum: report.rpIndex,
            rpNumSub: report.medIndex,
            key: report.key,
            isReported,
          })
      }
    },
    onSuccess: (_data, report) =>
      patchCachedPrescription(queryClient, prescription.id, report),
  })

  // 呼び出し側の形は共有 useReportCallbacks と揃えてある(ItemsPane の行を書き換えないため)。
  // mutateAsync ではなく mutate を使う — 失敗を呼び出し側に投げると ReportToggle の
  // onClick が rejected promise を返し、unhandled rejection になる。
  const prescId = prescription.id

  // 直近の失敗。**表示中の処方箋のものだけ**を見る — hook は行を切り替えても作り直されないので、
  // prescId で照合しないと別の処方箋で出た失敗が隣の行に残る。
  // 次に mutate が走れば isError は下りるので、成功した時点で消える。
  const failed =
    mutation.isError && mutation.variables?.prescId === prescId
      ? mutation.variables
      : null

  return {
    reportBasicResult: (key: BasicResultFieldKey, isReported: boolean) =>
      mutation.mutate({ scope: 'basic', prescId, key, isReported }),
    reportRPUnit: (
      rpIndex: number,
      key: ReportableRpKey,
      isReported: boolean
    ) => mutation.mutate({ scope: 'rp', prescId, rpIndex, key, isReported }),
    reportMedication: (
      rpIndex: number,
      medIndex: number,
      key: ReportableMedKey,
      isReported: boolean
    ) =>
      mutation.mutate({
        scope: 'med',
        prescId,
        rpIndex,
        medIndex,
        key,
        isReported,
      }),
    /** この行の報告が直近で失敗していれば、行に出す一言を返す。 */
    reportErrorOn: (target: Report) =>
      failed && isSameTarget(failed, target) ? REPORT_FAILED : undefined,
  }
}
